// Shared modal primitive — portal to document.body, focus trap, scroll lock,
// Escape + backdrop click to close, focus restore on unmount.
// The single overlay mechanism for console dialogs (ConfirmDialog,
// DemoVideoModal, CommandPalette all render through this).
import { useEffect, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video, audio, [tabindex]:not([tabindex="-1"])'

// .focus() on a detached or display:none element silently no-ops — verify the
// target can actually take focus before restoring to it.
function canReceiveFocus(el: HTMLElement | null | undefined): el is HTMLElement {
  if (!el || !el.isConnected) return false
  if (typeof el.checkVisibility === 'function') return el.checkVisibility()
  return el.getClientRects().length > 0 || getComputedStyle(el).display !== 'none'
}

type ModalA11y = { labelledBy: string; label?: never } | { label: string; labelledBy?: never }

export type ModalProps = {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** aria-describedby target for the dialog panel */
  describedBy?: string
  /** dialog panel classes (sizing/border/surface — position comes from the overlay) */
  className?: string
  /** positioning wrapper — defaults to centered; override for e.g. top-anchored palettes */
  overlayClassName?: string
  backdropClassName?: string
  backdropLabel?: string
  /** element focused when the dialog opens — defaults to first focusable inside */
  initialFocusRef?: RefObject<HTMLElement | null>
  /**
   * Fallback restore target — focus returns to the element focused at open; this
   * ref is used only when that element is detached/hidden (or never focused).
   */
  restoreFocusRef?: RefObject<HTMLElement | null>
  /**
   * Extra key handling while open (e.g. Enter-to-confirm). Runs before the
   * built-in Escape/Tab handling; call e.preventDefault() to suppress those.
   */
  onKeyDown?: (e: KeyboardEvent, dialog: HTMLElement | null) => void
} & ModalA11y

export function Modal({
  open,
  onClose,
  children,
  describedBy,
  className,
  overlayClassName,
  backdropClassName,
  backdropLabel,
  initialFocusRef,
  restoreFocusRef,
  onKeyDown,
  labelledBy,
  label,
}: ModalProps) {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  const prevFocusRef = useRef<HTMLElement | null>(null)
  // latest-ref pattern — effect stays bound to `open` only, so re-renders
  // while open don't re-run focus/scroll-lock setup
  const onCloseRef = useRef(onClose)
  const onKeyDownRef = useRef(onKeyDown)
  const initialFocusRefRef = useRef(initialFocusRef)
  const restoreFocusRefRef = useRef(restoreFocusRef)
  // The refs are refreshed after commit, not during render: a render can be
  // discarded, and a ref written during one would keep that discarded
  // render's value. Declared before the open effect so the write lands
  // before it reads the refs.
  useEffect(() => {
    onCloseRef.current = onClose
    onKeyDownRef.current = onKeyDown
    initialFocusRefRef.current = initialFocusRef
    restoreFocusRefRef.current = restoreFocusRef
  })

  useEffect(() => {
    if (!open) return
    prevFocusRef.current = document.activeElement as HTMLElement | null
    const focusTimer = window.setTimeout(() => {
      const target =
        initialFocusRefRef.current?.current ?? dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)
      target?.focus()
    }, 0)

    function onKey(e: KeyboardEvent) {
      onKeyDownRef.current?.(e, dialogRef.current)
      if (e.defaultPrevented) return
      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.tabIndex !== -1,
      )
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      const active = document.activeElement
      const inside = active instanceof Node && dialog.contains(active)
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)

    const prevBodyOverflow = document.body.style.overflow
    const prevDocOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevDocOverflow
      // The element focused at open wins — it is the trigger the user actually
      // activated. restoreFocusRef is only a fallback for when that element is
      // gone (detached/hidden) or was never meaningful (body — e.g. the dialog
      // was opened via keyboard shortcut with nothing focused).
      const prev = prevFocusRef.current
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- the fallback is read at CLOSE time on purpose: by then the trigger may be detached, and that is exactly the case this fallback exists for. Copying the value at open time would freeze it and defeat it.
      const preferred = restoreFocusRefRef.current?.current
      const target =
        prev && prev !== document.body && prev !== document.documentElement && canReceiveFocus(prev)
          ? prev
          : canReceiveFocus(preferred)
            ? preferred
            : null
      if (target) window.setTimeout(() => target.focus(), 0)
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', overlayClassName)}>
      <button
        type="button"
        tabIndex={-1}
        aria-label={backdropLabel ?? t('common.close')}
        onClick={() => onCloseRef.current()}
        className={cn('absolute inset-0 bg-black/60', backdropClassName)}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={label}
        aria-describedby={describedBy}
        className={cn('relative', className)}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
